import React, { useState, useEffect, ReactElement } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  Paper,
  Grid,
  makeStyles,
  Typography,
  TableCell,
  Button,
  Box,
  Badge,
  Tabs,
  Tab,
  Divider,
  Link,
} from "@material-ui/core";
import clsx from "clsx";
import useTable from "../../../hooks/table/useTable";
import useInput from "../../../hooks/input/useInput";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import TrTable from "../../../components/shared/controls/TrTable";
import TrAutocomplete from "../../../components/shared/controls/TrAutocomplete";
import LinkAdapter from "../../../components/shared/common/LinkAdapter";
import ButtonConfirmDialog from "../../shared/common/ButtonConfirmDialog";
import TrSelect from "../../shared/controls/TrSelect";
import TrSnackbar from "../../shared/controls/TrSnackbar";
import TrTableHeadMetadata from "../../../models/shared/TrTableHeadMetadata";
import TrTableBodyData from "../../../models/shared/TrTableBodyData";
import TrSelectData from "../../../models/shared/TrSelectData";
import SelectItem from "../../../models/shared/SelectItem";
import PaymentRequestFilterValue from "../../../models/admin/payment-request/PaymentRequestFilterValue";
import PaymentRequestFilter from "../../../models/admin/payment-request/PaymentRequestFilter";
import YearFilter from "../../../models/shared/YearFilter";
import MonthFilter from "../../../models/shared/MonthFilter";
import PaymentRequestList from "../../../models/admin/payment-request/PaymentRequestList";
import PaymentRequestListPagedResult from "../../../models/admin/payment-request/PaymentRequestListPagedResult";
import PaymentRequestStatus from "../../../models/payment-request-status/PaymentRequestStatusEnum";
import SchoolType from "../../../models/school-type/SchoolTypeEnum";
import PaymentRequestApiClient from "../../../api/admin/PaymentRequestApiClient";
import { nameof } from "../../../utilities/utilities";
import { adminPaymentRequestDetailsUrl } from "../../../urls";
import PaymentRequestStatusElement from "../../shared/controls/PaymentRequestStatusElement";

export default function PaymentRequestListPage({
  history,
}: RouteComponentProps) {
  const classes = useStyles({});

  const [tabIndex, setTabIndex] = useState(0);
  const [filters, setFilters] = useState<PaymentRequestFilter>(
    {} as PaymentRequestFilter
  );
  const [filterValue, setFilterValue] = useState<PaymentRequestFilterValue>(() => {
    const savedFilterValues = sessionStorage.getItem("adminPaymentRequestFilterValues");
    return savedFilterValues
      ? JSON.parse(savedFilterValues)
      : {} as PaymentRequestFilterValue;
  });

  const [paymentRequestsPaged, setPaymentRequestsPaged] = useState<
    PaymentRequestListPagedResult<PaymentRequestList>
  >({} as PaymentRequestListPagedResult<PaymentRequestList>);

  const { page, pageSize, handleChangePage, handleChangepageSize } = useTable();
  const { handleSelectChange } = useInput();
  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    close: closeSnackbar,
    message: snackbarMessage,
    variant: snackbarVariant,
  } = useSnackbar();

  const clearFilters = () => {
    const defaultFilterValue = getDefaultFilterValue(
      filterValue.schoolTypeId,
      filters
    );
    setFilterValue(defaultFilterValue);
    sessionStorage.setItem(
      "adminPaymentRequestFilterValues",
      JSON.stringify(defaultFilterValue)
    );
  };

  useEffect(() => {
    getFilters();
  }, [tabIndex]);

  useEffect(() => {
    if (filterValue.schoolTypeId && filterValue.schoolYearId) {
      search();
    }
  }, [filterValue, page, pageSize]);

  useEffect(() => {
    sessionStorage.setItem(
      "adminPaymentRequestFilterValues",
      JSON.stringify(filterValue)
    );
  }, [filterValue]);

  useEffect(() => {
    const savedFilterValues = sessionStorage.getItem(
      "adminPaymentRequestFilterValues"
    );
    if (savedFilterValues) {
      const parsedFilterValues = JSON.parse(savedFilterValues);
      if (parsedFilterValues.schoolTypeId === 1) {
        setTabIndex(0);
      }
      else if (parsedFilterValues.schoolTypeId === 2) { setTabIndex(1); }
    }
  }, []);
  async function getFilters() {
    const schoolTypeId =
      tabIndex === TabIndex.ElementarySchool
        ? SchoolType.Elementary
        : SchoolType.High;

    const apiClient = new PaymentRequestApiClient();
    const searchFilters = await apiClient.getSearchFilters(schoolTypeId);

    setFilters(searchFilters);

    setFilterValue((prev) => ({
      ...getDefaultFilterValue(schoolTypeId, searchFilters),
      ...prev,
    }));
  }

  function getDefaultFilterValue(
    schoolTypeId: number,
    searchFilters: PaymentRequestFilter
  ): PaymentRequestFilterValue {
    return {
      schoolTypeId: schoolTypeId,
      schoolId: "",
      schoolYearId:
        searchFilters.schoolYears.length > 0
          ? searchFilters.schoolYears[0].id
          : "",
      date: "",
      excludeStatus: Number(PaymentRequestStatus.Draft),
      status: undefined,
    };
  }

  async function search() {
    const apiClient = new PaymentRequestApiClient();
    const requests = await apiClient.search(filterValue, page + 1, pageSize);

    setPaymentRequestsPaged(requests);
  }

  const handleTabChange = (event: React.ChangeEvent<{}>, newIndex: number) => {
    setTabIndex(newIndex);
    filterValue.schoolTypeId = newIndex + 1;
  };

  const handleBodyCellCreated = (
    tableCell: ReactElement,
    rowData: PaymentRequestList,
    _cellData: any,
    headMetadata?: TrTableHeadMetadata<PaymentRequestList>
  ) => {
    let statusClassName = "";
    switch (rowData.statusId) {
      case PaymentRequestStatus.Submitted:
        statusClassName = clsx(classes.bold, classes.warning);
        break;
      case PaymentRequestStatus.Approved:
        statusClassName = clsx(classes.bold, classes.success);
        break;
      case PaymentRequestStatus.SentForPayment:
        statusClassName = clsx(classes.bold, classes.info);
        break;
      case PaymentRequestStatus.Rejected:
        statusClassName = clsx(classes.bold, classes.error);
        break;
      default:
        break;
    }

    if (headMetadata && !headMetadata.dataName) {
      return (
        <TableCell key={tableCell.key} {...tableCell.props}>
          <Button
            variant="outlined"
            size="small"
            color="primary"
            component={LinkAdapter}
            to={adminPaymentRequestDetailsUrl(
              rowData.paymentRequestId.toString()
            )}
          >
            PREGLED
          </Button>
        </TableCell>
      );
    } else {
      return (
        <TableCell
          key={tableCell.key}
          {...tableCell.props}
          className={clsx(
            headMetadata &&
            headMetadata.dataName === nameof<typeof rowData>("statusName") &&
            statusClassName
          )}
        />
      );
    }
  };

  const handleSend = async () => {
    const apiClient = new PaymentRequestApiClient();

    await apiClient.updateStatuses(filterValue).then(function (response) {
      openSnackbar("Uspješno ste izmijenili status zahtjeva.", "success");
    });

    search();
  };

  const tableHeadMetadata: TrTableHeadMetadata<PaymentRequestList>[] = [
    { id: "schoolName", label: "Škola", dataName: "schoolName" },
    { id: "schoolPlace", label: "Mjesto", dataName: "schoolPlace" },
    { id: "schoolYearName", label: "Šk. godina", dataName: "schoolYearName" },
    { id: "month", label: "Mjesec", dataName: "month" },
    { id: "studentsNumber", label: "Broj učenika", dataName: "studentsNumber" },
    { id: "resolvedCost", label: "Iznos", dataName: "resolvedCost" },
    {
      id: "status",
      label: "Status",
      dataName: "statusName",
      dataFunc: (p: PaymentRequestList) => (
        <PaymentRequestStatusElement
          statusId={p.statusId}
          statusName={p.statusName}
        />
      ),
    },
    {
      id: "action",
      label: "",
      dataFunc: (p: PaymentRequestList) => (
        <Button
          variant="outlined"
          size="small"
          color="primary"
          component={LinkAdapter}
          to={adminPaymentRequestDetailsUrl(p.paymentRequestId.toString())}
        >
          PREGLED
        </Button>
      ),
    },
  ];

  const tableBodyData: TrTableBodyData = {
    idDataName: "paymentRequestId",
    data: paymentRequestsPaged.data,
  };

  const getYearMonths = () => {
    let selectedYear: YearFilter | undefined;
    if (filterValue.schoolYearId && filters.schoolYears) {
      selectedYear = filters.schoolYears.find(
        (s) => s.id === filterValue.schoolYearId
      );
    }

    return (
      (selectedYear && [
        { startingDate: "", name: "" } as MonthFilter,
        ...selectedYear.months,
      ]) ||
      []
    );
  };

  const handleSchoolChange = (name: string, value: any) => {
    setFilterValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid container item spacing={2} xs={12}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5">Odobravanje mjesečnih zahtjeva</Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box textAlign="right" display="inline">
              <Typography>
                Pošalji na plaćanje sve odobrene zahtjeve
                <Badge
                  color="secondary"
                  badgeContent={paymentRequestsPaged.approvedCount}
                >
                  <ButtonConfirmDialog
                    btnClassName={classes.sendButton}
                    btnDisabled={
                      !paymentRequestsPaged.approvedCount ||
                      paymentRequestsPaged.approvedCount === 0
                    }
                    btnText={"Pošalji"}
                    title={"Slanje na plaćanje"}
                    message={"Želite li zahtjeve poslati na plaćanje?"}
                    cancelBtnText={"Odustani"}
                    okBtnText={"Pošalji"}
                    onOkClick={() => handleSend()}
                  />
                </Badge>
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Tabs value={tabIndex} onChange={handleTabChange}>
            <Tab label="Osnovna škola" />
            <Tab label="Srednja škola" />
          </Tabs>
        </Grid>
        <Grid container item spacing={2} xs={12}>
          <Grid item xs={12} md={3}>
            <TrAutocomplete
              label="Škola"
              name={nameof<typeof filterValue>("schoolId")}
              data={filters.schools}
              value={filterValue.schoolId}
              minimumInputLength={3}
              onChange={handleSchoolChange}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TrSelect
              label={"Šk. godina"}
              value={filterValue.schoolYearId}
              name={nameof<typeof filterValue>("schoolYearId")}
              fullWidth={true}
              selectData={
                {
                  valueProp: "id",
                  textProp: "name",
                  data: filters.schoolYears || [],
                } as TrSelectData<YearFilter>
              }
              onChange={(e) => handleSelectChange(e, setFilterValue)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TrSelect
              label={"Mjesec"}
              value={filterValue.date}
              name={nameof<typeof filterValue>("date")}
              fullWidth={true}
              selectData={
                {
                  valueProp: "startingDate",
                  textProp: "name",
                  data: getYearMonths() || [],
                } as TrSelectData<MonthFilter>
              }
              onChange={(e) => handleSelectChange(e, setFilterValue)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TrSelect
              label={"Status"}
              value={filterValue.status}
              name={nameof<typeof filterValue>("status")}
              fullWidth={true}
              selectData={
                {
                  valueProp: "value",
                  textProp: "text",
                  data: filters.statuses || [],
                } as TrSelectData<SelectItem<any>>
              }
              onChange={(e) => handleSelectChange(e, setFilterValue)}
            />
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ textAlign: 'right' }}>
          <Link component="button" onClick={clearFilters} variant="body2" style={{ cursor: 'pointer' }}>
            Očisti filtere
          </Link>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item md={12}>
          <Paper>
            <TrTable
              hover={true}
              headMetadata={tableHeadMetadata}
              bodyData={tableBodyData}
              enablePagination={true}
              page={page}
              pageSize={pageSize}
              totalCount={paymentRequestsPaged.count}
              onChangePage={handleChangePage}
              onChangePageSize={handleChangepageSize}
              noDataMessage={"Trenutno nema mjesečnih zahtjeva"}
            />
          </Paper>
        </Grid>
      </Grid>
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  sendButton: {
    marginLeft: "10px",
  },
  success: {
    color: "#43A047",
  },
  warning: {
    color: "#FFA047",
  },
  info: {
    color: theme.palette.primary.main,
  },
  error: {
    color: theme.palette.error.main,
  },
  bold: {
    fontWeight: "bold",
  },
}));

enum TabIndex {
  ElementarySchool = 0,
  HighSchool = 1,
}
